import dayjs from 'dayjs';

export const buildSicBoardPath = boardId => `/sic-scorecard/${boardId}`;

export const SIC_PASS_THRESHOLD = 0.9;

export const SIC_BOARD_TYPE = {
  balancedScorecard: 0,
  sic: 1
};

export const DEFAULT_WEEK_START_DAY = 1;

export const YES_NO_TARGET_TYPE_NAME = 'Yes/No';

export const DATE_TARGET_TYPE_NAME = 'Date';

export const TIME_TARGET_TYPE_NAME = 'Time';

export const RAG_TARGET_TYPE_NAME = 'RAG Rating';

export const RAG_TARGET_TYPE_VALUE = 'RAG';

export const SIC_VALUE_KIND = {
  number: 'number',
  yesNo: 'yesNo',
  date: 'date',
  time: 'time',
  rag: 'rag'
};

export const SIC_INPUT_METHOD = {
  autoPopulate: 1,
  manualEnter: 2,
  formula: 3
};

export const RAG_RATING = {
  green: 1,
  amber: 2,
  red: 3
};

export const RAG_RATING_OPTIONS = [
  { value: RAG_RATING.green, label: 'Green' },
  { value: RAG_RATING.amber, label: 'Amber' },
  { value: RAG_RATING.red, label: 'Red' }
];

export const ragRatingLabel = value =>
  RAG_RATING_OPTIONS.find(option => option.value === Number(value))?.label ??
  '';

const PERCENT_UOM = '%';

const STORED_DATE_FORMAT = 'YYYYMMDD';

const MINUTES_PER_HOUR = 60;

export const DAILY_SCHEDULE_TYPE_NAME = 'Daily';

export const SIC_COMPARE_TYPE = {
  greaterThan: 0,
  lessThan: 1,
  greaterOrEqual: 2,
  lessOrEqual: 3,
  equal: 4,
  none: 5
};

export const SIC_COMPARE_TYPES = [
  { value: SIC_COMPARE_TYPE.greaterThan, symbol: '>', label: '>' },
  { value: SIC_COMPARE_TYPE.lessThan, symbol: '<', label: '<' },
  { value: SIC_COMPARE_TYPE.greaterOrEqual, symbol: '>=', label: '>=' },
  { value: SIC_COMPARE_TYPE.lessOrEqual, symbol: '<=', label: '<=' },
  { value: SIC_COMPARE_TYPE.equal, symbol: '=', label: '=' },
  { value: SIC_COMPARE_TYPE.none, symbol: '-', label: 'None (display only)' }
];

export const WEEK_START_DAY_OPTIONS = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
];

export const SIC_API_DATE_FORMAT = 'YYYY-MM-DD';

const AVERAGED_UOMS = [PERCENT_UOM];

export const isBlankValue = value =>
  value === null || value === undefined || value === '';

export const toNumberOrNull = value => {
  if (isBlankValue(value)) return null;
  const number = Number(String(value).replace(/,/g, ''));
  return Number.isNaN(number) ? null : number;
};

export const resolveValueKind = (targetType, targetTypeIds) => {
  if (targetType === null || targetType === undefined) {
    return SIC_VALUE_KIND.number;
  }
  if (targetType === targetTypeIds.yesNo) return SIC_VALUE_KIND.yesNo;
  if (targetType === targetTypeIds.date) return SIC_VALUE_KIND.date;
  if (targetType === targetTypeIds.time) return SIC_VALUE_KIND.time;
  if (targetType === targetTypeIds.rag) return SIC_VALUE_KIND.rag;
  return SIC_VALUE_KIND.number;
};

export const hasFixedTarget = valueKind => valueKind === SIC_VALUE_KIND.yesNo;

export const isFormulaMeasurable = measurable =>
  measurable.inputMethod === SIC_INPUT_METHOD.formula;

export const parseFormulaJson = formulaJson => {
  if (isBlankValue(formulaJson)) return null;
  try {
    const parsedFormula = JSON.parse(formulaJson);
    return parsedFormula?.formulaWithIds ? parsedFormula : null;
  } catch {
    return null;
  }
};

export const hasTargetFormula = measurable =>
  parseFormulaJson(measurable.targetFormula) !== null;

export const isTargetEditable = measurable =>
  !hasFixedTarget(measurable.valueKind) && !hasTargetFormula(measurable);

export const isPercentUom = measurable =>
  measurable.displayUom?.trim() === PERCENT_UOM;

const roundToDecimals = (number, numOfDecimal) => {
  const decimals = Math.max(0, Number(numOfDecimal) || 0);
  return Number(number.toFixed(decimals));
};

const formatNumberWithUom = (value, measurable) => {
  const number = toNumberOrNull(value);
  const numberText =
    number === null
      ? String(value)
      : String(roundToDecimals(number, measurable.numOfDecimal));
  return isPercentUom(measurable) ? `${numberText}%` : numberText;
};

export const storedNumberToDate = storedValue => {
  const number = toNumberOrNull(storedValue);
  if (number === null) return null;
  const digits = String(Math.trunc(number));
  if (digits.length !== STORED_DATE_FORMAT.length) return null;
  const date = dayjs(
    `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`
  );
  return date.isValid() && date.format(STORED_DATE_FORMAT) === digits
    ? date
    : null;
};

export const dateToStoredNumber = date =>
  Number(dayjs(date).format(STORED_DATE_FORMAT));

export const storedMinutesToTimeText = storedValue => {
  const totalMinutes = toNumberOrNull(storedValue);
  if (totalMinutes === null) return '';
  const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  const minutes = Math.round(totalMinutes % MINUTES_PER_HOUR);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const timeTextToStoredMinutes = timeText => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(timeText ?? '');
  if (!match) return null;
  return Number(match[1]) * MINUTES_PER_HOUR + Number(match[2]);
};

const isWorkingDay = date => date.day() !== 0 && date.day() !== 6;

export const countWorkingDaysAfter = (fromDate, toDate) => {
  const start = dayjs(fromDate).startOf('day');
  const end = dayjs(toDate).startOf('day');
  const direction = end.isBefore(start) ? -1 : 1;
  let workingDays = 0;
  for (
    let cursor = start;
    !cursor.isSame(end, 'day');
    cursor = cursor.add(direction, 'day')
  ) {
    const nextDay = cursor.add(direction, 'day');
    const dayToCount = direction === 1 ? nextDay : cursor;
    if (isWorkingDay(dayToCount)) workingDays += direction;
  }
  return workingDays;
};

export const addWorkingDays = (fromDate, workingDays) => {
  let cursor = dayjs(fromDate).startOf('day');
  let remaining = workingDays;
  while (remaining > 0) {
    cursor = cursor.add(1, 'day');
    if (isWorkingDay(cursor)) remaining -= 1;
  }
  return cursor;
};

const comparableValueFor = (value, measurable, dayIndex) => {
  if (measurable.valueKind === SIC_VALUE_KIND.date) {
    const enteredDate = storedNumberToDate(value);
    const rowDate = measurable.rowDates?.[dayIndex];
    if (!enteredDate || !rowDate) return null;
    return countWorkingDaysAfter(rowDate, enteredDate);
  }
  return toNumberOrNull(value);
};

export const formatValueDisplay = (value, measurable) => {
  if (isBlankValue(value)) return '';
  switch (measurable.valueKind) {
    case SIC_VALUE_KIND.yesNo:
      return Number(value) === 1 ? 'Yes' : 'No';
    case SIC_VALUE_KIND.date:
      return storedNumberToDate(value)?.format('DD-MMM') ?? '';
    case SIC_VALUE_KIND.time:
      return storedMinutesToTimeText(value);
    case SIC_VALUE_KIND.rag:
      return ragRatingLabel(value);
    default:
      return formatNumberWithUom(value, measurable);
  }
};

export const compareTypeSymbol = compareType =>
  SIC_COMPARE_TYPES.find(option => option.value === compareType)?.symbol ??
  '-';

export const buildWeekDates = (selectedDate, weekStartDay) => {
  const selected = dayjs(selectedDate).startOf('day');
  const daysSinceWeekStart = (selected.day() - weekStartDay + 7) % 7;
  const weekStart = selected.subtract(daysSinceWeekStart, 'day');
  return Array.from({ length: 7 }, (_, dayIndex) =>
    weekStart.add(dayIndex, 'day')
  );
};

export const buildMonthStartsOfWeek = weekDates => [
  ...new Set(
    weekDates.map(date => date.startOf('month').format(SIC_API_DATE_FORMAT))
  )
];

export const isMeasurableCountedInSic = measurable =>
  measurable.affectsSic && measurable.compareType !== SIC_COMPARE_TYPE.none;

export const isTargetAchieved = (value, target, measurable, dayIndex) => {
  if (isBlankValue(value)) return null;
  if (measurable.valueKind === SIC_VALUE_KIND.yesNo) {
    return Number(value) === 1;
  }

  const numericValue = comparableValueFor(value, measurable, dayIndex);
  const numericTarget = toNumberOrNull(target);
  if (numericValue === null || numericTarget === null) return null;

  switch (measurable.compareType) {
    case SIC_COMPARE_TYPE.greaterThan:
      return numericValue > numericTarget;
    case SIC_COMPARE_TYPE.lessThan:
      return numericValue < numericTarget;
    case SIC_COMPARE_TYPE.greaterOrEqual:
      return numericValue >= numericTarget;
    case SIC_COMPARE_TYPE.lessOrEqual:
      return numericValue <= numericTarget;
    case SIC_COMPARE_TYPE.equal:
      return numericValue === numericTarget;
    default:
      return null;
  }
};

export const isMeasurableAchievedOnDay = (measurable, dayIndex) =>
  isMeasurableCountedInSic(measurable)
    ? isTargetAchieved(
        measurable.values[dayIndex],
        measurable.targets[dayIndex],
        measurable,
        dayIndex
      )
    : null;

const average = numbers =>
  numbers.length
    ? numbers.reduce((sum, number) => sum + number, 0) / numbers.length
    : null;

export const calculateSectorSicScore = (measurables, dayIndex) => {
  const results = measurables
    .map(measurable => isMeasurableAchievedOnDay(measurable, dayIndex))
    .filter(result => result !== null);

  if (!results.length) return null;
  return results.filter(Boolean).length / results.length;
};

export const calculateDailySicScore = (sectors, dayIndex) =>
  average(
    sectors
      .map(sector => calculateSectorSicScore(sector.measurables, dayIndex))
      .filter(score => score !== null)
  );

export const calculateWeeklyAverage = dailyScores =>
  average(dailyScores.filter(score => score !== null));

export const calculateMeasurableWeekTotal = measurable => {
  const enteredValues = measurable.values.filter(value => !isBlankValue(value));
  if (!enteredValues.length) return null;

  if (
    measurable.valueKind === SIC_VALUE_KIND.date ||
    measurable.valueKind === SIC_VALUE_KIND.time
  ) {
    return null;
  }

  if (measurable.valueKind === SIC_VALUE_KIND.yesNo) {
    const yesCount = enteredValues.filter(value => Number(value) === 1).length;
    return `${yesCount}/${enteredValues.length}`;
  }

  if (measurable.valueKind === SIC_VALUE_KIND.rag) {
    const greenCount = enteredValues.filter(
      value => Number(value) === RAG_RATING.green
    ).length;
    return `${greenCount}/${enteredValues.length} Green`;
  }

  const numericValues = enteredValues
    .map(toNumberOrNull)
    .filter(value => value !== null);
  if (!numericValues.length) return null;

  const total = AVERAGED_UOMS.includes(measurable.displayUom)
    ? average(numericValues)
    : numericValues.reduce((sum, value) => sum + value, 0);
  return formatNumberWithUom(total, {
    ...measurable,
    numOfDecimal: Math.max(Number(measurable.numOfDecimal) || 0, 2)
  });
};

export const formatSicPercent = score =>
  score === null || score === undefined ? '-' : `${Math.round(score * 100)}%`;

export const isSicScorePassing = score =>
  score !== null && score >= SIC_PASS_THRESHOLD;

export const formatSingleTarget = (target, measurable) => {
  if (isBlankValue(target)) return '';
  const { valueKind } = measurable;
  if (valueKind === SIC_VALUE_KIND.rag) return ragRatingLabel(target);
  if (valueKind === SIC_VALUE_KIND.date) {
    const workingDays = toNumberOrNull(target);
    return workingDays === null ? '' : `+${workingDays} working days`;
  }
  if (valueKind === SIC_VALUE_KIND.time) {
    return storedMinutesToTimeText(target);
  }
  return formatNumberWithUom(target, measurable);
};

export const formatTargetDisplay = measurable => {
  if (measurable.valueKind === SIC_VALUE_KIND.yesNo) return 'Yes';
  const distinctTargets = [
    ...new Set(
      measurable.targets
        .filter(target => !isBlankValue(target))
        .map(target => formatSingleTarget(target, measurable))
    )
  ];
  if (!distinctTargets.length) return 'TBC';
  return distinctTargets.join(' / ');
};

export const buildSicSectors = ({
  sectors,
  measurables,
  targets,
  dataItems,
  weekDates,
  targetTypeIds
}) => {
  const targetByMeasurableAndMonth = new Map(
    targets.map(target => [
      `${target.measurableId}|${dayjs(target.date).format('YYYY-MM')}`,
      target.target
    ])
  );
  const valueByMeasurableAndDate = new Map(
    dataItems.map(item => [
      `${item.measurableId}|${dayjs(item.date).format(SIC_API_DATE_FORMAT)}`,
      item.value
    ])
  );

  return sectors.map(sector => ({
    ...sector,
    measurables: measurables
      .filter(measurable => measurable.sectorId === sector.id)
      .map(measurable => {
        const valueKind = resolveValueKind(
          measurable.targetType,
          targetTypeIds
        );
        const isYesNo = valueKind === SIC_VALUE_KIND.yesNo;
        return {
          ...measurable,
          valueKind,
          rowDates: weekDates,
          displayUom: isYesNo
            ? 'Yes/No'
            : measurable.uom?.trim() || measurable.targetTypeValue,
          targets: weekDates.map(date =>
            isYesNo
              ? 1
              : targetByMeasurableAndMonth.get(
                  `${measurable.id}|${date.format('YYYY-MM')}`
                ) ?? null
          ),
          values: weekDates.map(
            date =>
              valueByMeasurableAndDate.get(
                `${measurable.id}|${date.format(SIC_API_DATE_FORMAT)}`
              ) ?? null
          )
        };
      })
  }));
};
